import { processPropsSchema } from "../../lib/utils/dataSchema/processPropsSchema";
import { cloneDeep, forEach, isPlainObject } from "lodash-es";
import { getPatchedDataSchema } from '@tencent/data-schema-core';
import { getCompletedSlotMeta } from "../../lib/utils/components";
import { CastConstructor } from "../../lib/utils/types";
import { getPatchedVueComponent } from './patchVueComponentSource';
function defaultFitData(meta, req) {
  const {
    mainBindingId
  } = meta;
  if (!mainBindingId) return null;
  const binding = meta.bindings.find(x => x.id === mainBindingId);
  if (binding.checkData && !binding.checkData(req.dataSchema)) return null;
  const layout = {
    type: meta.id,
    bindings: {
      [mainBindingId]: req.dataPath
    }
  };
  if (binding.onBind) binding.onBind(req, layout);
  return {
    type: binding.event ? 'editor-alt' : 'display',
    name: meta.name,
    layout
  };
}
export const defaultDesignerFlags = {
  allowAdjustHeight: true,
  allowAdjustWidth: true,
  allowFloating: true,
  keepAspectRatio: false,
  adjustMinHeight: false,
  adjustMinWidth: false
};
/** @internal */
const CompletedComponentMetaBase = CastConstructor(function (meta) {
  var _meta$display, _meta$visible, _meta$isCoreComponent, _meta$methods;
  Object.assign(this, meta);
  this.rawMeta = meta;
  this.type = meta.type || 'normal';
  this.display = (_meta$display = meta.display) !== null && _meta$display !== void 0 ? _meta$display : 'block';
  this.visible = (_meta$visible = meta.visible) !== null && _meta$visible !== void 0 ? _meta$visible : true;
  this.isCoreComponent = !!((_meta$isCoreComponent = meta.isCoreComponent) !== null && _meta$isCoreComponent !== void 0 ? _meta$isCoreComponent : true);
  this.slots = Array.isArray(meta.slots) ? meta.slots.map(getCompletedSlotMeta) : meta.slots;
  this.methods = (_meta$methods = meta.methods) !== null && _meta$methods !== void 0 && _meta$methods.length ? getPatchedDataSchema({
    type: 'object',
    methods: meta.methods
  }).methods : [];
  // 注意！！ this.propsSchema 要在 CompletedComponentMeta 里面处理！！！
});
export class CompletedComponentMeta extends CompletedComponentMetaBase {
  constructor(meta) {
    super(meta);
    /**
     * 根据 propName 反查相关的 BindingMeta
     */
    this.propBindings = {};
    /**
     * (兼容性问题保留的东西，没啥用)
     */
    this.meta = this;
    // --------------------------
    // 组件魔改，注入一些默认逻辑
    this.component = getPatchedVueComponent(this.component);
    // --------------------------
    const tmp = processPropsSchema(meta.propsSchema);
    this.propsSchema = tmp.newDataSchema;
    this.defaultProps = tmp.defaultValue;
    this.allowInteropProps = tmp.allowInteropFields;
    // --------------------------
    const {
      bindings
    } = meta;
    if (bindings) {
      var _bindings$find;
      const mainBinding = (_bindings$find = bindings.find(x => x.main)) !== null && _bindings$find !== void 0 ? _bindings$find : bindings.find(x => x.required);
      if (mainBinding) {
        this.mainBinding = mainBinding;
        this.mainBindingId = mainBinding.id;
        if (!mainBinding.main) mainBinding.main = true;
      }
      forEach(bindings, binding => {
        const {
          pathProp,
          prop,
          schemaProp,
          stubProp
        } = binding;
        if (pathProp) this.propBindings[pathProp] = binding;
        if (prop) this.propBindings[prop] = binding;
        if (schemaProp) this.propBindings[schemaProp] = binding;
        if (stubProp) this.propBindings[stubProp] = binding;
      });
    }
    // --------------------------
    if (!this.fitData) this.fitData = req => defaultFitData(this, req);
  }
  getSlots(x) {
    const {
      slots
    } = this;
    if (!slots) return null;
    if (Array.isArray(slots)) return slots; // 注意，前面 constructor 里已经 getCompletedSlotMeta 过
    if (typeof slots === 'function') try {
      var _slots;
      return (_slots = slots(x)) === null || _slots === void 0 ? void 0 : _slots.filter(Boolean).map(getCompletedSlotMeta);
    } catch (err) {
      // invalid slots
      console.error(`[UICore] Component ${this.rawMeta.id} getSlot failed. `, err);
    }
  }
  getDesignerFlags(x) {
    const {
      rawMeta: meta,
      display
    } = this;
    const designerFlags = meta.designerFlags;
    const base = {
      ...defaultDesignerFlags,
      allowFloating: (display !== null && display !== void 0 ? display : 'block') === 'block' && !!meta.slots
    };
    if (!designerFlags) return base;
    if (typeof designerFlags === 'function') {
      return {
        ...base,
        ...designerFlags({
          ...x,
          props: {
            ...this.defaultProps,
            ...x.props
          }
        })
      };
    }
    return {
      ...base,
      ...designerFlags
    };
  }
  /**
   * 按逻辑顺序，根据默认值做补全。。。 注意，这里会直接修改入参 layout：
   *
   * 1. 如果有定义 initialLayout，则根据他先补全 props
   *
   * 注意，这里只关注这一个组件，不关注子组件。
   * 如果需要，请见 `ucBus` 上面的 `completeLayout`
   */
  completeLayout(layout) {
    // 0. 检查 layout.id 是不是当前组件，如果不是，就不做任何事情
    if (layout.type !== this.id) return;
    // 1. 先根据 initialLayout 完成
    const {
      initialLayout
    } = this;
    if (initialLayout) {
      forEach(initialLayout, (valueRaw, key) => {
        // eslint-disable-next-line eqeqeq
        if (layout[key] == null) {
          // 如果当前 layout 没有配置这个东西，就补充上
          layout[key] = cloneDeep(valueRaw);
        } else if (isPlainObject(valueRaw)) {
          // 否则，如果是 object 就 assign
          layout[key] = {
            ...cloneDeep(valueRaw),
            ...layout[key]
          };
        }
        // 否则，可能是一个数组 / 字符串 etc，那么理论上应该在前面 == null 的时候做好决策了
      });
    }
  }
}